import Script from 'next/script';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useConfig } from '../../../utils/hooks/useConfig';

declare global {
  interface Window {
    turnstile: {
      render: (
        idOrContainer: string | HTMLElement,
        options: {
          sitekey: string;
          action?: string;
          theme?: 'light' | 'dark' | 'auto';
          callback: (token: string) => void;
        },
      ) => string;
      reset: (widgetIdOrContainer: string | HTMLElement) => void;
      getResponse: (widgetIdOrContainer: string | HTMLElement) => string | undefined;
      remove: (widgetIdOrContainer: string | HTMLElement) => void;
    };
  }
}

type Props = {
  onTokenSet: (token?: string) => void;
  action?: string;
  theme?: 'light' | 'dark' | 'auto';
};

export type TurnstileRef = { reset: () => void };

const Turnstile = forwardRef<TurnstileRef, Props>(({ onTokenSet, action, theme }, ref) => {
  const { config } = useConfig();
  const [isScriptReady, setIsScriptReady] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const token = useRef<string>();
  const widgetId = useRef<string>();

  const handleOnReady = useCallback(() => {
    setIsScriptReady(true);
  }, []);

  useEffect(() => {
    // Test tokens can be found https://developers.cloudflare.com/turnstile/frequently-asked-questions/#are-there-sitekeys-and-secret-keys-that-can-be-used-for-testing
    const SITE_KEY = config?.TURNSTILE_SITE_KEY;
    if (isScriptReady && !isRendered && SITE_KEY) {
      setIsRendered(true);
      widgetId.current = window.turnstile.render('#cf-turnstile-container', {
        sitekey: SITE_KEY,
        action: action || 'milkywire.com',
        theme,
        callback: (newToken) => {
          token.current = newToken;
          onTokenSet(token.current);
        },
      });
    }
  }, [isScriptReady, onTokenSet, action, theme, isRendered, token, config]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      widgetId.current && window.turnstile.reset(widgetId.current);
    },
  }));

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js"
        strategy="afterInteractive"
        onReady={handleOnReady}
      />
      <div id="cf-turnstile-container" />
    </>
  );
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
